import {
  Alert,
  Button,
  DialogActionTrigger,
  DialogTitle,
  Flex,
  IconButton,
  Input,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { FiEye, FiEyeOff } from "react-icons/fi"

import type { SettingsUpdate } from "@/client"
import { OrganizationsService } from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"

interface OrganizationSettingsProps {
  orgId: string
  orgName: string
  currentSettings: Record<string, any>
}

type OrganizationSettingsFormValues = Omit<
  SettingsUpdate,
  "feedback_enabled" | "flashcards_enabled" | "flashcard_leaderboard_enabled"
> & {
  feedback_enabled: boolean
  flashcards_enabled: boolean
  flashcard_leaderboard_enabled: boolean
  openai_api_key?: string | null
  student_labels: {
    dashboard_label: string
    quizzes_label: string
    practice_quizzes_label: string
    attempts_label: string
    feedback_label: string
    flashcards_label: string
    settings_label: string
  }
}

function OrganizationSettings({
  orgId,
  orgName,
  currentSettings,
}: OrganizationSettingsProps) {
  const [isOpen, setIsOpen] = useState(false)
  const computedDefaultValues = useMemo(() => {
    const embedDomains = Array.isArray(currentSettings.embed_domains)
      ? currentSettings.embed_domains
      : []

    return {
      practice_test_limit:
        typeof currentSettings.practice_test_limit === "number"
          ? currentSettings.practice_test_limit
          : null,
      embed_domains: embedDomains,
      feedback_enabled:
        typeof currentSettings.feedback_enabled === "boolean"
          ? currentSettings.feedback_enabled
          : true,
      flashcards_enabled:
        typeof currentSettings.flashcards_enabled === "boolean"
          ? currentSettings.flashcards_enabled
          : true,
      flashcard_leaderboard_enabled:
        typeof currentSettings.flashcard_leaderboard_enabled === "boolean"
          ? currentSettings.flashcard_leaderboard_enabled
          : true,
      openai_api_key:
        typeof currentSettings.openai_api_key === "string"
          ? currentSettings.openai_api_key
          : "",
      student_labels: {
        dashboard_label:
          typeof currentSettings.student_labels?.dashboard_label === "string"
            ? currentSettings.student_labels.dashboard_label
            : "",
        quizzes_label:
          typeof currentSettings.student_labels?.quizzes_label === "string"
            ? currentSettings.student_labels.quizzes_label
            : "",
        practice_quizzes_label:
          typeof currentSettings.student_labels?.practice_quizzes_label ===
          "string"
            ? currentSettings.student_labels.practice_quizzes_label
            : "",
        attempts_label:
          typeof currentSettings.student_labels?.attempts_label === "string"
            ? currentSettings.student_labels.attempts_label
            : "",
        feedback_label:
          typeof currentSettings.student_labels?.feedback_label === "string"
            ? currentSettings.student_labels.feedback_label
            : "",
        flashcards_label:
          typeof currentSettings.student_labels?.flashcards_label === "string"
            ? currentSettings.student_labels.flashcards_label
            : "",
        settings_label:
          typeof currentSettings.student_labels?.settings_label === "string"
            ? currentSettings.student_labels.settings_label
            : "",
      },
    }
  }, [currentSettings])

  const [domains, setDomains] = useState<string[]>(
    computedDefaultValues.embed_domains,
  )
  const [newDomain, setNewDomain] = useState("")
  const [showOpenAIKey, setShowOpenAIKey] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<OrganizationSettingsFormValues>({
    mode: "onBlur",
    defaultValues: computedDefaultValues,
  })

  const flashcardsEnabled = watch("flashcards_enabled")

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setDomains(computedDefaultValues.embed_domains)
    reset(computedDefaultValues)
  }, [computedDefaultValues, isOpen, reset])

  const mutation = useMutation({
    mutationFn: (data: OrganizationSettingsFormValues) => {
      const studentLabels = {
        dashboard_label: data.student_labels.dashboard_label.trim() || null,
        quizzes_label: data.student_labels.quizzes_label.trim() || null,
        practice_quizzes_label:
          data.student_labels.practice_quizzes_label.trim() || null,
        attempts_label: data.student_labels.attempts_label.trim() || null,
        feedback_label: data.student_labels.feedback_label.trim() || null,
        flashcards_label: data.student_labels.flashcards_label.trim() || null,
        settings_label: data.student_labels.settings_label.trim() || null,
      }
      const requestBody = {
        ...data,
        embed_domains: domains,
        student_labels: studentLabels,
      }

      return OrganizationsService.updateOrganizationSettings({
        orgId,
        requestBody,
      })
    },
    onSuccess: () => {
      showSuccessToast("Settings updated successfully")
      setIsOpen(false)
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
      queryClient.invalidateQueries({
        queryKey: ["currentOrganization", orgId],
      })
    },
  })

  const onSubmit = (data: OrganizationSettingsFormValues) => {
    mutation.mutate(data)
  }

  const addDomain = () => {
    if (newDomain && !domains.includes(newDomain)) {
      setDomains([...domains, newDomain])
      setNewDomain("")
    }
  }

  const removeDomain = (domain: string) => {
    setDomains(domains.filter((d) => d !== domain))
  }

  return (
    <DialogRoot
      size={{ base: "sm", md: "lg" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          Settings
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Organization Settings - {orgName}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={6} align="stretch">
              <VStack gap={4} align="stretch">
                <Text fontSize="lg" fontWeight="bold">
                  Student Labels
                </Text>
                <Text fontSize="sm" color="fg.muted">
                  Customize the student menu items and page headers.
                </Text>
                <Field label="Dashboard menu and header">
                  <Input
                    {...register("student_labels.dashboard_label")}
                    placeholder="Dashboard"
                    maxLength={80}
                  />
                </Field>
                <Field label="Quiz menu and header">
                  <Input
                    {...register("student_labels.quizzes_label")}
                    placeholder="Take Quiz"
                    maxLength={80}
                  />
                </Field>
                <Field label="Practice quiz menu and header">
                  <Input
                    {...register("student_labels.practice_quizzes_label")}
                    placeholder="Practice Quizzes"
                    maxLength={80}
                  />
                </Field>
                <Field label="Attempts menu and header">
                  <Input
                    {...register("student_labels.attempts_label")}
                    placeholder="My Attempts"
                    maxLength={80}
                  />
                </Field>
                <Field label="Feedback menu and header">
                  <Input
                    {...register("student_labels.feedback_label")}
                    placeholder="My Feedback"
                    maxLength={80}
                  />
                </Field>
                <Field label="Flashcard menu and header">
                  <Input
                    {...register("student_labels.flashcards_label")}
                    placeholder="Flashcards"
                    maxLength={80}
                  />
                </Field>
                <Field label="Settings menu and header">
                  <Input
                    {...register("student_labels.settings_label")}
                    placeholder="User Settings"
                    maxLength={80}
                  />
                </Field>
              </VStack>

              <Field
                invalid={!!errors.practice_test_limit}
                errorText={errors.practice_test_limit?.message}
                label="Practice Test Limit"
                helperText="Maximum number of practice tests students can take"
              >
                <Input
                  id="practice_test_limit"
                  {...register("practice_test_limit", {
                    valueAsNumber: true,
                    min: { value: 0, message: "Must be positive" },
                  })}
                  placeholder="e.g., 50"
                  type="number"
                />
              </Field>

              <Field
                label="Feedback"
                helperText="Allow students to submit feedback during review."
              >
                <Controller
                  control={control}
                  name="feedback_enabled"
                  render={({ field }) => (
                    <Checkbox
                      name={field.name}
                      checked={field.value}
                      onCheckedChange={(details) =>
                        field.onChange(details.checked === true)
                      }
                    >
                      Enable feedback
                    </Checkbox>
                  )}
                />
              </Field>

              <Field
                label="Flashcards"
                helperText="Enable flashcard decks and spaced-repetition study for this organization."
              >
                <Controller
                  control={control}
                  name="flashcards_enabled"
                  render={({ field }) => (
                    <Checkbox
                      name={field.name}
                      checked={field.value}
                      onCheckedChange={(details) =>
                        field.onChange(details.checked === true)
                      }
                    >
                      Enable flashcards
                    </Checkbox>
                  )}
                />
              </Field>

              <Field
                label="Flashcard Leaderboard"
                helperText={
                  flashcardsEnabled
                    ? "Show a program-scoped leaderboard ranking students by XP."
                    : "Enable flashcards to use this setting."
                }
              >
                <Controller
                  control={control}
                  name="flashcard_leaderboard_enabled"
                  render={({ field }) => (
                    <Checkbox
                      name={field.name}
                      checked={field.value && flashcardsEnabled}
                      disabled={!flashcardsEnabled}
                      onCheckedChange={(details) =>
                        field.onChange(details.checked === true)
                      }
                    >
                      Enable flashcard leaderboard
                    </Checkbox>
                  )}
                />
              </Field>

              <Field
                label="Embed Domains"
                helperText="Domains allowed to embed tests"
              >
                <VStack gap={2} align="stretch">
                  <Flex gap={2}>
                    <Input
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      placeholder="example.com"
                      type="text"
                    />
                    <Button onClick={addDomain} type="button">
                      Add Domain
                    </Button>
                  </Flex>
                  {domains.map((domain) => (
                    <Flex
                      key={domain}
                      justify="space-between"
                      align="center"
                      p={2}
                      bg="bg.muted"
                      borderRadius="md"
                    >
                      <Text>{domain}</Text>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => removeDomain(domain)}
                        type="button"
                      >
                        Remove
                      </Button>
                    </Flex>
                  ))}
                </VStack>
              </Field>

              {/* AI Features Section */}
              <VStack gap={4} align="stretch">
                <Text fontSize="lg" fontWeight="bold">
                  AI Features
                </Text>

                <Alert.Root status="info">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Description fontSize="sm">
                      Configure your organization's OpenAI API key to enable
                      AI-powered features like rationale generation. If not
                      configured, the system-level key will be used (if
                      available).
                    </Alert.Description>
                  </Alert.Content>
                </Alert.Root>

                <Field
                  label="OpenAI API Key (Optional)"
                  helperText="Your API key is encrypted and never shared"
                >
                  <Flex gap={2}>
                    <Input
                      {...register("openai_api_key")}
                      type={showOpenAIKey ? "text" : "password"}
                      placeholder="sk-..."
                      autoComplete="off"
                    />
                    <IconButton
                      aria-label={
                        showOpenAIKey ? "Hide API key" : "Show API key"
                      }
                      onClick={() => setShowOpenAIKey(!showOpenAIKey)}
                      variant="ghost"
                      type="button"
                    >
                      {showOpenAIKey ? <FiEyeOff /> : <FiEye />}
                    </IconButton>
                  </Flex>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Get your API key at{" "}
                    <Link
                      href="https://platform.openai.com/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      color="blue.500"
                      textDecoration="underline"
                    >
                      platform.openai.com/api-keys
                    </Link>
                  </Text>
                </Field>
              </VStack>
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <DialogActionTrigger asChild>
              <Button
                variant="subtle"
                colorPalette="gray"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button variant="solid" type="submit" loading={isSubmitting}>
              Save Settings
            </Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  )
}

export default OrganizationSettings
